import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './BannerAdmin.css';

const API = process.env.REACT_APP_API_URL;

const POSITIONS = [
  { key: 'auth_left',   label: 'Login — Izquierda' },
  { key: 'auth_right',  label: 'Login — Derecha'   },
  { key: 'feed_right',  label: "Q's — Derecha"     },
  { key: 'price_right', label: 'Precio — Derecha'  },
];

const token = () => localStorage.getItem('access_token');
const authHeader = () => ({ Authorization: `Bearer ${token()}` });

const BannerAdmin = () => {
  const [activePos, setActivePos]   = useState(POSITIONS[0].key);
  const [banners, setBanners]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const [editBanner, setEditBanner] = useState(null);
  const [form, setForm]             = useState({ image_url: '', link_url: '', duration: 5, order: 0 });
  const [uploading, setUploading]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState(null);
  const fileRef = useRef(null);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/banners/admin/`, { headers: authHeader() });
      setBanners(r.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBanners(); }, []);

  const positionBanners = banners.filter(b => b.position === activePos);

  const openNew = () => {
    setEditBanner(null);
    setForm({ image_url: '', link_url: '', duration: 5, order: positionBanners.length });
    setShowForm(true);
    setMsg(null);
  };

  const openEdit = (b) => {
    setEditBanner(b);
    setForm({ image_url: b.image_url, link_url: b.link_url, duration: b.duration, order: b.order });
    setShowForm(true);
    setMsg(null);
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await axios.post(`${API}/banners/upload/`, fd, { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, image_url: r.data.image_url }));
      setMsg({ type: 'success', text: 'Imagen subida correctamente.' });
    } catch {
      setMsg({ type: 'error', text: 'Error al subir imagen.' });
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.image_url) { setMsg({ type: 'error', text: 'Sube o pega una URL de imagen.' }); return; }
    setSaving(true);
    setMsg(null);
    try {
      const payload = { ...form, position: activePos };
      if (editBanner) {
        await axios.patch(`${API}/banners/admin/${editBanner.id}/`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/banners/admin/`, payload, { headers: authHeader() });
      }
      await fetchBanners();
      setShowForm(false);
      setMsg({ type: 'success', text: editBanner ? 'Banner actualizado.' : 'Banner creado.' });
    } catch { setMsg({ type: 'error', text: 'Error al guardar.' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este banner?')) return;
    try {
      await axios.delete(`${API}/banners/admin/${id}/`, { headers: authHeader() });
      setBanners(prev => prev.filter(b => b.id !== id));
    } catch {}
  };

  const handleToggle = async (b) => {
    try {
      await axios.patch(`${API}/banners/admin/${b.id}/`, { is_active: !b.is_active }, { headers: authHeader() });
      setBanners(prev => prev.map(x => x.id === b.id ? { ...x, is_active: !x.is_active } : x));
    } catch {}
  };

  return (
    <div className="ba-wrap">
      <h3 className="ba-title">Gestión de Banners</h3>

      {/* Position tabs */}
      <div className="ba-tabs">
        {POSITIONS.map(p => (
          <button
            key={p.key}
            className={`ba-tab ${activePos === p.key ? 'ba-tab--active' : ''}`}
            onClick={() => { setActivePos(p.key); setShowForm(false); setMsg(null); }}
          >
            {p.label}
            <span className="ba-tab-count">{banners.filter(b => b.position === p.key).length}</span>
          </button>
        ))}
      </div>

      {/* Feedback */}
      {msg && (
        <div className={`ba-msg ba-msg--${msg.type}`}>{msg.text}</div>
      )}

      {/* Banner list */}
      {loading ? (
        <div className="ba-loading">Cargando…</div>
      ) : (
        <div className="ba-list">
          {positionBanners.length === 0 && !showForm && (
            <div className="ba-empty">Sin banners para esta posición. Los banners por defecto de BanaPrecios se mostrarán.</div>
          )}
          {positionBanners.map(b => (
            <div key={b.id} className={`ba-item ${!b.is_active ? 'ba-item--inactive' : ''}`}>
              <img src={b.image_url} alt="banner" className="ba-item-thumb" />
              <div className="ba-item-info">
                <div className="ba-item-pos">{POSITIONS.find(p => p.key === b.position)?.label}</div>
                {b.link_url && <div className="ba-item-link">{b.link_url.slice(0, 40)}…</div>}
                <div className="ba-item-meta">{b.duration}s · Orden {b.order}</div>
              </div>
              <div className="ba-item-actions">
                <button
                  className={`ba-toggle-btn ${b.is_active ? 'ba-toggle-btn--on' : ''}`}
                  onClick={() => handleToggle(b)}
                  title={b.is_active ? 'Desactivar' : 'Activar'}
                >
                  {b.is_active ? '●' : '○'}
                </button>
                <button className="ba-edit-btn" onClick={() => openEdit(b)}>Editar</button>
                <button className="ba-del-btn" onClick={() => handleDelete(b.id)}>Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit form */}
      {showForm ? (
        <div className="ba-form">
          <h4 className="ba-form-title">{editBanner ? 'Editar banner' : 'Nuevo banner'} — {POSITIONS.find(p => p.key === activePos)?.label}</h4>

          {/* Image upload */}
          <label className="ba-label">Imagen del banner</label>
          <div className="ba-image-row">
            {form.image_url && (
              <img src={form.image_url} alt="preview" className="ba-preview" />
            )}
            <div className="ba-image-controls">
              <button
                type="button"
                className="ba-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Subiendo…' : 'Subir imagen'}
              </button>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUploadImage} />
              <div className="ba-or">— o pegar URL directamente —</div>
              <input
                className="ba-input"
                type="url"
                placeholder="https://res.cloudinary.com/..."
                value={form.image_url}
                onChange={e => setForm(prev => ({ ...prev, image_url: e.target.value }))}
              />
            </div>
          </div>

          <label className="ba-label">Enlace (URL de destino)</label>
          <input
            className="ba-input"
            type="url"
            placeholder="https://empresa.com (opcional)"
            value={form.link_url}
            onChange={e => setForm(prev => ({ ...prev, link_url: e.target.value }))}
          />

          <div className="ba-form-row">
            <div>
              <label className="ba-label">Duración (segundos)</label>
              <input
                className="ba-input ba-input--sm"
                type="number"
                min="2"
                max="30"
                value={form.duration}
                onChange={e => setForm(prev => ({ ...prev, duration: parseInt(e.target.value) || 5 }))}
              />
            </div>
            <div>
              <label className="ba-label">Orden</label>
              <input
                className="ba-input ba-input--sm"
                type="number"
                min="0"
                value={form.order}
                onChange={e => setForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>

          <div className="ba-form-actions">
            <button className="ba-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando…' : editBanner ? 'Guardar cambios' : 'Crear banner'}
            </button>
            <button className="ba-cancel-btn" onClick={() => { setShowForm(false); setMsg(null); }}>
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button className="ba-add-btn" onClick={openNew}>+ Agregar banner</button>
      )}
    </div>
  );
};

export default BannerAdmin;
